/* eslint-disable react-hooks/exhaustive-deps */
import { Loading } from '@livecontrol/core-ui';
import type { Filters } from '@livecontrol/scheduler/components';
import { Scheduler } from '@livecontrol/scheduler/store';
import _ from 'lodash';
import { DateTime } from 'luxon';
import type { HTMLAttributes, ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Store } from '../../../store';
import { NoMatch } from '../common';
import { EventsSection } from '../event-rows';
import type { Event, Location } from '../store';
import { T_Sorting } from '../store';
import { Actions } from './actions';

const limitPage = 20;
const today = DateTime.now().toUTC();
/* const toEndGte = DateTime.now().plus({hour: 6}).toUTC(); */

export const Past = (props: HTMLAttributes<HTMLDivElement>): ReactElement => {
  const [page, setPage] = useState(1);
  const [date_lte, setDate_lte] = useState<DateTime | undefined>();
  const [end_lte, setEnd_lte] = useState<DateTime | undefined>();
  const [date_gte, setDate_gte] = useState<DateTime | undefined>();
  const [keyword, setKeyword] = useState<string | undefined>();
  const [keywordValue, setKeywordValue] = useState<string | undefined>();
  const [initialState, setInitialState] = useState(true);

  const [sorting, setSorting] = useState<T_Sorting>({
    key: 'start_time',
    direction: T_Sorting.Direction.DESCENDING
  });

  const [date, setDate] = useState<Filters.Period.Value | undefined>();
  const [locationSelected, setlocationSelected] = useState<Location | undefined>();
  const [locationId, setlocationId] = useState<string[] | undefined>();
  const [eventToScroll, setEventToScroll] = useState<Event[]>([]);

  const account = Store.Account.useAccount();

  const { events, refetch, loading } = Scheduler.Event.useEvents({
    event: {
      extra_filters: {
        page,
        limit: limitPage,
        client_id: account.id,
        date_lte,
        date_gte,
        end_lte,
        wildcard: keyword,
        sort_field: sorting.key,
        sort_order: sorting.direction,
        location_ids: locationId,
        state: 'past'
      }
    }
  });

  const handleRefetch = (): void => {
    if (events && refetch && !loading && events.hasMore) {
      void refetch({
        event: {
          extra_filters: {
            page,
            limit: limitPage,
            client_id: account.id,
            date_lte,
            date_gte,
            end_lte,
            wildcard: keyword,
            sort_field: sorting.key,
            sort_order: sorting.direction,
            location_ids: locationId,
            state: 'past'
          }
        }
      });
    }
  };

  const fetchMoreData = (): void => {
    handleRefetch();
    setPage((prevIndex) => prevIndex + 1);
  };

  const handleKeyword = useCallback(
    _.debounce((value: string) => {
      setKeyword(value);
      setPage(1);
    }, 500),
    []
  );

  const onChangeKeyword = (event: React.ChangeEvent<HTMLInputElement>): void => {
    handleKeyword(event.target.value);
    setKeywordValue(event.target.value);
  };

  const onChangeDate = (v?: Filters.Period.Value): void => {
    setDate(v);

    if (v?.max && v.min) {
      setDate_gte(DateTime.fromJSDate(v.min));
      setDate_lte(DateTime.fromJSDate(v.max));
      setEnd_lte(undefined);
    } else {
      setDate_gte(undefined);
      setDate_lte(today);
      setEnd_lte(undefined);
    }

    setPage(1);
  };

  const onChangeLocation = (value?: Location | Location[]): void => {
    setlocationSelected(Array.isArray(value) ? value[0] : value);

    if (Array.isArray(value)) {
      setlocationId(() => [value[0].id]);
    } else {
      setlocationId(() => (value ? [value.id] : undefined));
    }

    setPage(1);
  };

  const onChangeSorting = (value?: T_Sorting): void => {
    if (value) {
      setSorting(value);
      setPage(1);
    }
  };

  useEffect(() => {
    setPage(1);
    setEventToScroll([]);
    handleRefetch();
  }, [keyword, date_gte, date_lte, locationId, sorting]);

  const onFiltersReset = (): void => {
    setKeyword(undefined);
    setDate_lte(undefined);
    setEnd_lte(undefined);
    setDate_gte(undefined);
    setKeywordValue(undefined);
    setDate(undefined);
    setlocationId(undefined);
    onChangeLocation(undefined);

    setSorting({
      key: 'start_time',
      direction: T_Sorting.Direction.ASCENDING
    });
  };

  useEffect(() => {
    if (events) {
      setInitialState(false);

      setEventToScroll((oldState) => {

        const newEvents = events.events ? [...oldState, ...events.events] : [...oldState];
        const mapIds = new Map<number, Event>();

        newEvents.forEach((obj) => {
          mapIds.set(obj.id, obj);
        });

        const newState = [...mapIds.values()];

        return [...newState];
      });
    }
  }, [events]);

  return (
    <div {...props}>
      {initialState ? (
        <Loading.Delay />
      ) : (
        <>
          <Actions
            onChangeKeyword={onChangeKeyword}
            onChangeDate={onChangeDate}
            valueDate={date}
            onChangeLocation={onChangeLocation}
            valueLocation={locationSelected}
            valueKeyword={keywordValue}
            onChangeSorting={onChangeSorting}
            valueSorting={sorting}
            className='mb-32px'
          />
          {eventToScroll.length > 0 && events ? (
            <EventsSection
              transformed={eventToScroll}
              fetchMoreData={fetchMoreData}
              hasMore={events.hasMore ?? false}
            />
          ) : loading ? (
            <Loading.Delay />
          ) : (
            <NoMatch phase={3} onFiltersReset={onFiltersReset} />
          )}
        </>
      )}
    </div>
  );
};
